import type { SurveyAttribute, SurveyAttributeOptionScores } from '@engagement-web-app/common';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Ranking, RankingSkeleton } from '@/components/surveys/common/Ranking';

type RankingsByDepartmentProps = {
    /** `null` while still loading. */
    attributes: SurveyAttribute[] | null;
    /** `null` while still loading. */
    scores: SurveyAttributeOptionScores[] | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function RankingsByDepartment({ attributes, scores, isError, onRetry }: RankingsByDepartmentProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="組織別ランキングの取得に失敗しました。" />;

    if (!attributes) return <RankingsByDepartmentSkeleton />;

    // Resolved before `scores` is checked: whether the organization has a department
    // attribute at all is known as soon as `attributes` lands and isn't a loading
    // state. The scores query is also disabled without a setting id, so `scores`
    // would stay null forever here and the skeleton would never resolve. Unlike the
    // system side there's no notice — client users can't edit survey settings.
    if (!attributes.some((s) => s.type === 'DEPARTMENT')) return null;

    if (!scores) return <RankingsByDepartmentSkeleton />;

    return <RankingsByDepartmentContent scores={scores} />;
}

type RankingsByDepartmentContentProps = {
    scores: SurveyAttributeOptionScores[];
};

function RankingsByDepartmentContent({ scores }: RankingsByDepartmentContentProps) {
    return (
        <Flex gap="4">
            <Ranking title="よかった組織" variant="positive" items={scores} limit={10} />
            <Ranking title="改善必要組織" variant="negative" items={scores} limit={10} />
        </Flex>
    );
}

export function RankingsByDepartmentSkeleton() {
    return (
        <Flex gap="4">
            <RankingSkeleton limit={10} />
            <RankingSkeleton limit={10} />
        </Flex>
    );
}
